/**
 * Table des vehicules.
 *
 * Un vehicule se construit comme un batiment (meme menu, meme paiement en
 * ressources) mais il se pilote ensuite. Il n'y en a que deux, et ils sont
 * volontairement complementaires: la voiture va vite sur la terre, la barque est
 * le seul moyen d'aller vraiment au large. Aucun des deux ne sait faire le
 * travail de l'autre (voir `milieu`), et c'est ce qui leur donne un sens.
 *
 * Couts: la voiture est l'objet le plus cher du jeu, devant la villa
 * (bois 80, pierre 40, minerai 8). Le chiffre qui compte est le minerai, qui ne
 * tombe qu'une fois sur trois en minant un rocher, et un rocher rend 2 pierre:
 * 100 pierre = ~50 rochers = ~17 minerai. Demander 100 pierre et 20 minerai
 * fait donc mordre les deux en meme temps, sans qu'une seule matiere devienne
 * l'unique goulot (avec 40 minerai il aurait fallu ~120 rochers, soit 240
 * pierre: trois fois trop de pierre pour rien).
 *
 * La barque, elle, ne coute QUE du bois et de la fibre: ni pierre ni minerai,
 * qui n'existent qu'a la montagne et au glacier. On peut donc la batir depuis la
 * plage de depart, ce qui en fait la premiere grande construction du jeu.
 */
import type { VehiculeDef, VehiculeKind } from '../types/monde.ts'

export const VEHICULES: Record<VehiculeKind, VehiculeDef> = {
  voiture: {
    kind: 'voiture',
    nom: 'Voiture',
    feminin: true,
    icone: '🚗',
    cout: { bois: 70, pierre: 100, minerai: 20, fibre: 20 },
    // Une vraie voiture fait 1.8 x 4.3 m: on garde ces proportions, elles
    // rendent la silhouette lisible de dessus a 45 degres.
    taille: { l: 2, p: 4.2 },
    vitesse: 3,
    milieu: 'terre',
    peutPecher: false,
  },
  barque: {
    kind: 'barque',
    nom: 'Barque',
    feminin: true,
    icone: '🛶',
    cout: { bois: 34, fibre: 20 },
    taille: { l: 1.6, p: 3.6 },
    vitesse: 2,
    milieu: 'eau',
    peutPecher: true,
  },

  /*
   * LES TROIS AVIONS. Ils sont RIGOUREUSEMENT interchangeables — meme emprise,
   * meme vitesse, meme pilotage — et ne different que par le prix et par la
   * silhouette. C'est la commande du proprietaire (« different models and
   * colors but functionally the same for now »), et c'est aussi ce qui garde le
   * pilotage dans UN seul fichier: shared/sim/avion.ts ne nomme aucun modele.
   *
   * L'emprise (6 x 6 m) est celle de l'envergure sur la longueur: un petit
   * appareil, aussi large que long, ce qui se lit tout de suite d'en haut. Elle
   * tient dans la largeur de la piste (6 m) et commande l'ecart entre deux
   * appareils gares (`MARGE_VEHICULE`): deux places de stationnement sont a dix
   * metres l'une de l'autre, soit un de plus que ce qu'elles exigent.
   *
   * Ils ne se posent QU'A UN AEROPORT: c'est `validerPlacementVehicule` qui le
   * dit, et c'est la seule chose qui les rende dependants d'un batiment.
   */
  avion: {
    kind: 'avion',
    nom: 'Avion',
    icone: '✈️',
    cout: { bois: 60, pierre: 40, minerai: 40, fibre: 25 },
    taille: { l: 6, p: 6 },
    // CINQ FOIS la marche, comme demande. Le chiffre vit aussi dans
    // `V_CROISIERE` (shared/sim/avion.ts), qui le derive de WALK_SPEED: celui-ci
    // sert au plafond de vitesse du serveur et a l'affichage.
    vitesse: 5,
    milieu: 'air',
    peutPecher: false,
  },
  biplan: {
    kind: 'biplan',
    nom: 'Biplan',
    icone: '🛩️',
    // Du bois et de la toile: le moins cher des trois en minerai, le plus
    // gourmand en bois et en fibre. On peut donc l'avoir sans avoir beaucoup mine.
    cout: { bois: 90, fibre: 45, pierre: 20, minerai: 25 },
    taille: { l: 6, p: 6 },
    vitesse: 5,
    milieu: 'air',
    peutPecher: false,
  },
  jet: {
    kind: 'jet',
    nom: 'Jet',
    icone: '🛫',
    // Le plus cher du catalogue apres l'hotel: c'est l'objet de fin de partie.
    cout: { bois: 40, pierre: 60, minerai: 70, fibre: 30 },
    taille: { l: 6, p: 6 },
    vitesse: 5,
    milieu: 'air',
    peutPecher: false,
  },
}

/** Vrai si ce modele vole: une seule question, une seule reponse. */
export function estAvion(k: VehiculeKind): boolean {
  return VEHICULES[k].milieu === 'air'
}

export function vehiculeDef(k: VehiculeKind): VehiculeDef {
  return VEHICULES[k]
}

/**
 * Ordre d'affichage dans le menu de construction: du plus accessible au plus
 * cher, comme MAISONS dans shared/data/batiments.ts.
 */
export const VEHICULES_MENU: readonly VehiculeKind[] = ['barque', 'voiture', 'biplan', 'avion', 'jet']

/**
 * Hauteur du pilote au-dessus du plancher du vehicule, en metres.
 *
 * Ne vit pas dans VehiculeDef (shared/types/monde.ts n'est pas a moi) mais le
 * client en a besoin: sans ce decalage, le bonhomme assis se retrouve enterre
 * dans la carrosserie ou sous la coque.
 */
export const HAUTEUR_ASSISE: Record<VehiculeKind, number> = {
  voiture: 0.95,
  barque: 0.3,
  // Dans un avion, le pilote est assis DANS la carlingue, un peu haut: le
  // cockpit d'un petit appareil est au-dessus de l'aile.
  avion: 1.05,
  biplan: 1.0,
  jet: 1.15,
}
